import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { formatNewsMonthYear } from "@/lib/displayOrderDates";
import { maxPublicBatchAlYear } from "@/lib/publicBatchVisibility";
import zcmHeroBg from "@/images/zcm-background.webp";
import zahiraObaPhoto from "@/images/zahira-oba.webp";
import rizmyPhoto from "@/images/members/rizmy.jpg";

type NewsItem = {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  slug: string | null;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

type BranchRef = { name: string; slug: string } | null;
type BatchRef = {
  slug: string;
  custom_title: string | null;
  official_title: string | null;
  al_year: number | null;
} | null;
type AssociationRef = { name: string; slug: string } | null;

type AffiliatedNewsItem = NewsItem & {
  branch: BranchRef;
  batch: BatchRef;
  association: AssociationRef;
};

const ASSOCIATION_INTRO =
  "The Old Boys’ Association of Zahira College Mawanella brings together alumni who share a bond to our school and to one another. We support current students, strengthen networks between batches and branches, and preserve the spirit of our alma mater through service, camaraderie, and lifelong friendship.";

const GENERAL_SECRETARY_MESSAGE =
  "As General Secretary of the Old Boys’ Association, Zahira College Mawanella, I welcome every old boy to stand with us in serving Mother Zahira. By staying connected through branches, batches, and events, we strengthen our school, support today’s students, and carry the spirit of Zahira forward. Your participation—whether large or small—helps us build a purposeful, united association we can all be proud of.";

function one<T>(x: T | T[] | null | undefined): T | null {
  if (x == null) return null;
  return Array.isArray(x) ? (x[0] ?? null) : x;
}

function batchLabel(b: NonNullable<BatchRef>) {
  return b.custom_title || b.official_title || "Batch";
}

function affiliatedBadge(
  n: Pick<AffiliatedNewsItem, "branch" | "batch" | "association">
): { label: string; href: string } | null {
  if (n.branch) return { label: n.branch.name, href: `/alumni/branches/${n.branch.slug}` };
  if (n.batch) return { label: batchLabel(n.batch), href: `/alumni/batches/${n.batch.slug}` };
  if (n.association) return { label: n.association.name, href: `/alumni/associations/${n.association.slug}` };
  return null;
}

export function HomePage() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [affiliatedNews, setAffiliatedNews] = useState<AffiliatedNewsItem[]>([]);

  useEffect(() => {
    void supabase
      .from("news")
      .select("id,title,description,image_url,slug,published_at,created_at,updated_at")
      .is("branch_id", null)
      .is("batch_id", null)
      .is("association_id", null)
      .order("updated_at", { ascending: false })
      .limit(4)
      .then(({ data }) => setNews((data as NewsItem[]) ?? []));

    void supabase
      .from("news")
      .select(
        `
        id,
        title,
        description,
        image_url,
        slug,
        published_at,
        created_at,
        updated_at,
        branch:branches(name, slug),
        batch:batches(slug, custom_title, official_title, al_year),
        association:associations(name, slug)
      `
      )
      .or("branch_id.not.is.null,batch_id.not.is.null,association_id.not.is.null")
      .order("updated_at", { ascending: false })
      .limit(4)
      .then(({ data }) => {
        const list = (data ?? []) as {
          id: string;
          title: string;
          description: string | null;
          image_url: string | null;
          slug: string | null;
          published_at: string | null;
          created_at: string | null;
          updated_at: string | null;
          branch: BranchRef | BranchRef[];
          batch: BatchRef | BatchRef[];
          association: AssociationRef | AssociationRef[];
        }[];
        const cutoff = maxPublicBatchAlYear();
        setAffiliatedNews(
          list
            .map((r) => ({
              id: r.id,
              title: r.title,
              description: r.description,
              image_url: r.image_url,
              slug: r.slug,
              published_at: r.published_at,
              created_at: r.created_at,
              updated_at: r.updated_at,
              branch: one(r.branch),
              batch: one(r.batch),
              association: one(r.association),
            }))
            .filter((r) => {
              const b = r.batch;
              if (!b) return true;
              if (b.al_year == null) return true;
              return b.al_year <= cutoff;
            })
        );
      });
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden bg-brand-blue text-brand-gray">
        <div className="absolute inset-0 bg-brand-blue" aria-hidden />
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center mix-blend-luminosity opacity-[0.5]"
          style={{ backgroundImage: `url(${zcmHeroBg})` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-blue/74 via-brand-blue/52 to-brand-blue/82"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.14] mix-blend-soft-light bg-[radial-gradient(circle_at_20%_20%,#820000_0%,transparent_50%),radial-gradient(circle_at_80%_60%,#ebebeb_0%,transparent_45%)]"
          aria-hidden
        />
        <div className="relative mx-auto max-w-6xl px-4 py-20 md:py-28 [text-shadow:0_2px_18px_rgba(0,0,0,0.45),0_1px_2px_rgba(0,0,0,0.55)]">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-brand-gray">
            Who we are
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-tight text-white md:text-5xl">
            A fellowship of Zahirians, united across generations and borders.
          </h1>
          <div className="mt-10">
            <h2 className="font-display text-2xl font-bold text-white md:text-3xl">
              Join our Alumni Membership
            </h2>
            <a
              href="https://circle.zcmoba.com/"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-maroon px-8 py-2.5 text-sm font-bold uppercase tracking-wide text-white shadow-xl transition hover:bg-brand-maroon/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Register Now
              <span aria-hidden>→</span>
            </a>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-b border-brand-gray/80 bg-gradient-to-b from-brand-gray/40 via-white to-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.4] [background-image:radial-gradient(circle_at_1px_1px,rgba(4,49,97,0.06)_1px,transparent_0)] [background-size:22px_22px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-32 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-brand-maroon/[0.04] blur-3xl"
          aria-hidden
        />
        <div className="relative mx-auto max-w-6xl px-4 py-12 md:py-16">
          <div className="grid gap-10 md:grid-cols-2 md:items-start md:gap-14 lg:gap-16">
            <div className="relative min-w-0 pl-5 sm:pl-7">
              <div
                className="absolute left-0 top-1 h-24 w-px bg-gradient-to-b from-brand-maroon/70 via-brand-maroon/25 to-transparent sm:top-2 sm:h-32"
                aria-hidden
              />
              <span className="inline-flex rounded-full border border-brand-maroon/15 bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-maroon shadow-sm backdrop-blur-sm">
                Old Boys&apos; Association
              </span>
              <h2 className="mt-4 max-w-xl font-display text-3xl font-bold leading-[1.12] tracking-tight text-brand-blue md:text-4xl">
                Together for Zahira College Mawanella
              </h2>

              <div
                className="mt-9 flex items-center gap-4"
                aria-hidden
              >
                <span className="h-px flex-1 max-w-[4.5rem] bg-gradient-to-r from-brand-maroon/50 to-transparent" />
                <span className="font-display text-[10px] font-bold uppercase tracking-[0.35em] text-brand-blue/35">
                  Our story
                </span>
                <span className="h-px flex-1 bg-gradient-to-l from-brand-blue/12 to-transparent" />
              </div>

              <p className="mt-6 max-w-xl text-[15px] leading-[1.72] text-brand-blue/76">
                {ASSOCIATION_INTRO}
              </p>

              <Link
                to="/about"
                className="group mt-8 inline-flex items-center gap-2.5 rounded-full border border-brand-maroon/25 bg-white/90 px-5 py-2.5 text-sm font-semibold text-brand-maroon shadow-sm backdrop-blur-sm transition hover:border-brand-maroon hover:bg-brand-maroon hover:text-white hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
              >
                About the association
                <span
                  aria-hidden
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-maroon/10 text-xs transition group-hover:bg-white/20 group-hover:text-white"
                >
                  →
                </span>
              </Link>
            </div>

            <div className="flex min-w-0 w-full max-w-lg flex-col self-start md:ml-auto md:max-w-none">
              <figure className="relative w-full shrink-0">
                <div
                  className="pointer-events-none absolute -inset-1 rounded-[1.15rem] bg-gradient-to-br from-brand-maroon/10 via-transparent to-brand-blue/10 opacity-70 blur-md"
                  aria-hidden
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/90 bg-brand-gray/20 shadow-[0_20px_50px_-24px_rgba(4,49,97,0.28)] ring-1 ring-brand-blue/10">
                  <img
                    src={zahiraObaPhoto}
                    alt="Zahira College Mawanella and the Old Boys' Association"
                    className="block h-auto w-full max-w-full"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </figure>

              <div className="mt-6 grid w-full grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-maroon">
                    Service
                  </p>
                  <p className="mt-2 text-sm leading-snug text-brand-blue/88">
                    Old Boys · Zahira College
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-maroon">
                    Community
                  </p>
                  <p className="mt-2 text-sm leading-snug text-brand-blue/88">
                    Branch · Batch · Associations
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-gray/50 py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-maroon">
                OBA News
              </p>
              <h2 className="font-display text-3xl font-bold text-brand-blue">
                Latest updates
              </h2>
            </div>
            <Link
              to="/news"
              className="hidden text-sm font-semibold text-brand-maroon hover:underline sm:inline"
            >
              View all
            </Link>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {news.length === 0 && (
              <p className="text-sm text-brand-blue/70 col-span-full">
                No news yet — add items from the admin console.
              </p>
            )}
            {news.map((n) => {
              const dateLine = formatNewsMonthYear(n.updated_at ?? n.created_at);
              return (
              <article
                key={n.id}
                className="flex flex-col overflow-hidden rounded-xl border border-brand-gray bg-white shadow-sm"
              >
                <div className="aspect-[4/3] bg-brand-gray">
                  {n.image_url ? (
                    <img
                      src={n.image_url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-brand-blue/50">
                      No image
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  {dateLine ? (
                    <p className="text-xs font-semibold uppercase tracking-wide text-brand-maroon">
                      {dateLine}
                    </p>
                  ) : null}
                  <h3 className="font-display text-lg font-bold text-brand-blue line-clamp-2">
                    {n.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm text-brand-blue/80 line-clamp-3">
                    {n.description}
                  </p>
                  <Link
                    to={n.slug ? `/news/${n.slug}` : "/news"}
                    className="mt-4 text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
                  >
                    Read more
                  </Link>
                </div>
              </article>
            );
            })}
          </div>
          <Link
            to="/news"
            className="mt-8 inline-block text-sm font-semibold text-brand-maroon hover:underline sm:hidden"
          >
            View all news →
          </Link>
        </div>
      </section>

      <section
        className="relative overflow-hidden border-b border-brand-gray/70 bg-white py-16 md:py-20"
        aria-labelledby="general-secretary-message-heading"
      >
        <div
          className="pointer-events-none absolute right-0 top-0 h-72 w-72 translate-x-1/3 -translate-y-1/4 rounded-full bg-brand-maroon/[0.06] blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute bottom-0 left-0 h-64 w-64 -translate-x-1/3 translate-y-1/4 rounded-full bg-brand-blue/[0.05] blur-3xl"
          aria-hidden
        />
        <span
          className="pointer-events-none absolute left-0 top-24 z-0 font-display text-[clamp(5.5rem,20vw,10rem)] font-bold leading-none text-brand-maroon/[0.05] select-none sm:top-28 md:left-[2%] md:top-28 md:text-[clamp(7rem,18vw,11rem)] lg:left-[4%]"
          aria-hidden
        >
          &ldquo;
        </span>

        <div className="relative z-10 mx-auto max-w-6xl px-4 text-left">
          <span
            id="general-secretary-message-heading"
            className="inline-flex rounded-full border border-brand-maroon/15 bg-brand-maroon/[0.07] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-maroon"
          >
            General Secretary&apos;s message
          </span>

          <div className="mt-10 grid gap-10 md:mt-12 md:grid-cols-12 md:items-start md:gap-12 lg:gap-14">
            <div className="md:col-span-3 lg:col-span-3">
              <div className="relative w-full max-w-[180px] sm:max-w-[200px] md:max-w-[200px]">
                <div
                  className="pointer-events-none absolute -inset-2 rounded-[1.15rem] bg-gradient-to-br from-brand-maroon/15 via-transparent to-brand-blue/10 opacity-70 blur-lg"
                  aria-hidden
                />
                <img
                  src={rizmyPhoto}
                  alt="Rizmy Razik, General Secretary of the OBA"
                  className="relative aspect-[4/5] w-full rounded-xl object-cover shadow-[0_12px_32px_-16px_rgba(4,49,97,0.22)] ring-1 ring-brand-blue/10"
                />
              </div>
            </div>

            <div className="min-w-0 md:col-span-9 lg:col-span-9">
              <p className="max-w-prose font-sans text-[17px] font-normal leading-[1.75] text-brand-blue/85 md:text-lg">
                {GENERAL_SECRETARY_MESSAGE}
              </p>
              <div className="mt-10 max-w-prose">
                <div
                  className="h-0.5 w-12 bg-gradient-to-r from-brand-maroon to-brand-maroon/30"
                  aria-hidden
                />
                <p className="mt-6 font-display text-2xl font-bold tracking-tight text-brand-blue md:text-3xl">
                  Rizmy Razik
                </p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-maroon/90">
                  General Secretary · OBA Zahira College Mawanella
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-gray/40 py-14 md:py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-maroon">
                Affiliated bodies
              </p>
              <h2 className="font-display text-3xl font-bold text-brand-blue">
                Affiliated news
              </h2>
              <p className="mt-2 text-sm text-brand-blue/75">
                Updates from branches, batches, and associations.
              </p>
            </div>
            <Link
              to="/news/other"
              className="hidden text-sm font-semibold text-brand-maroon hover:underline sm:inline"
            >
              View all
            </Link>
          </div>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {affiliatedNews.length === 0 && (
              <p className="text-sm text-brand-blue/60 col-span-full">
                No affiliated news published yet.
              </p>
            )}

            {affiliatedNews.map((n) => {
              const badge = affiliatedBadge(n);
              const dateLine = formatNewsMonthYear(n.updated_at ?? n.created_at);
              return (
                <article
                  key={n.id}
                  className="flex flex-col overflow-hidden rounded-xl border border-brand-gray bg-white shadow-sm"
                >
                  <div className="aspect-[4/3] bg-brand-gray">
                    {n.image_url ? (
                      <img
                        src={n.image_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-brand-blue/50">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    {badge ? (
                      <Link
                        to={badge.href}
                        className="text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
                      >
                        {badge.label}
                      </Link>
                    ) : null}
                    {dateLine ? (
                      <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-brand-maroon/80">
                        {dateLine}
                      </p>
                    ) : null}
                    <h3 className="mt-2 font-display text-lg font-bold text-brand-blue line-clamp-2">
                      {n.title}
                    </h3>
                    <p className="mt-2 flex-1 text-sm text-brand-blue/80 line-clamp-3">
                      {n.description}
                    </p>
                    <Link
                      to={n.slug ? `/news/other/${n.slug}` : "/news/other"}
                      className="mt-4 text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
                    >
                      Read more
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>

          <Link
            to="/news/other"
            className="mt-8 inline-block text-sm font-semibold text-brand-maroon hover:underline sm:hidden"
          >
            View all affiliated news →
          </Link>
        </div>
      </section>
    </div>
  );
}
